/**
 * BeneficiariesDataTable - Beneficiaries table using the reusable V2 DataTable component
 * Replaces the old card-based TableBeneficiaries for the V2 spend-plan layout.
 */

// react
import React, { useCallback, useContext, useMemo, useState } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	alpha,
	Avatar,
	Box,
	Chip,
	CircularProgress,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	Business as IconBusiness,
	CheckCircle as IconCheckCircle,
	DoNotDisturb as IconDeactivate,
	Edit as IconEdit,
	HourglassEmpty as IconPending,
	People as IconBeneficiary,
	PlayArrow as IconActivate,
	PriorityHigh as IconWarning,
	UploadFile as IconUpload,
	Visibility as IconView,
} from '@mui/icons-material';

// context
import { ContextProject } from '../../Project/Project.context';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// components
import {
	ColumnDef,
	DataTable,
	FilterConfig,
	RowAction,
	SortDirection,
} from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// types
import { CustomColumnDefinition, Document, Entity, User } from '../../../../@types';
import { formatDate } from '../../../../utils/dates';

// Types

interface BeneficiariesDataTableProps {
	/** Beneficiaries data */
	beneficiaries: Entity[];
	/** Whether data is loading */
	isLoading?: boolean;
	/** Current search value */
	searchValue?: string;
	/** Search change handler */
	onSearchChange?: (value: string) => void;
	/** Current sort column */
	orderBy?: string;
	/** Current sort direction */
	order?: SortDirection;
	/** Sort change handler */
	onSortChange?: (orderBy: string, order: SortDirection) => void;
	/** Current page (1-indexed) */
	page?: number;
	/** Rows per page */
	rowsPerPage?: number;
	/** Total count for pagination */
	totalCount?: number;
	/** Page change handler */
	onPageChange?: (page: number) => void;
	/** Rows per page change handler */
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	/** Current filter values */
	filterValues?: Record<string, string | number>;
	/** Filter change handler */
	onFilterChange?: (key: string, value: string | number) => void;
	/** Row action: Edit */
	onEditBeneficiary?: (entity: Entity) => void;
	/** Row action: Deactivate/Activate */
	onDeactivateBeneficiary?: (entity: Entity) => void;
	/** Row action: Upload Documents */
	onUploadDocuments?: (entity: Entity) => void;
	/** Custom field definitions for beneficiary entity type */
	customFieldDefinitions?: CustomColumnDefinition[];
}

// Helpers

const getStatusConfig = (status: string) => {
	switch (status) {
		case 'ACTIVE':
			return { color: '#16a34a', icon: IconCheckCircle, label: 'Active' };
		case 'INACTIVE':
			return { color: '#dc2626', icon: IconWarning, label: 'Inactive' };
		case 'PENDING':
			return { color: '#d97706', icon: IconPending, label: 'Pending' };
		default:
			return { color: '#6b7280', icon: IconPending, label: status };
	}
};

// Expanded Row Component

const ExpandedBeneficiaryRow = ({ entity }: { entity: Entity }) => {
	const [tabValue, setTabValue] = useState<'documents' | 'users'>('documents');
	const [documents, setDocuments] = useState<Document[]>([]);
	const [users, setUsers] = useState<User[]>([]);

	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const headers = useMemo(
		() => ({ Authorization: `Bearer ${accessToken}`, User: String(idToken) }),
		[accessToken, idToken]
	);

	const { isLoading: isLoadingDocs } = useFetch({
		isEnabled: Boolean(entity && tabValue === 'documents'),
		url: `${process.env.REACT_APP_API_URL}/documents?entities=${entity.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`,
		options: { headers },
		onSuccess: (res) => {
			if (res?.data) setDocuments(res.data);
		},
	});

	const { isLoading: isLoadingUsers } = useFetch({
		isEnabled: Boolean(entity && tabValue === 'users'),
		url: `${process.env.REACT_APP_API_URL}/users?entities=${entity.id}&populate=country`,
		options: { headers },
		onSuccess: (res) => {
			if (res?.data) setUsers(res.data);
		},
	});

	const cellSx = { py: 1, px: 2, fontSize: '0.8125rem', borderBottom: '1px solid #f3f4f6' };
	const headSx = { ...cellSx, fontWeight: 700, color: 'text.secondary', bgcolor: '#fafafa' };

	return (
		<Box>
			<Tabs
				value={tabValue}
				onChange={(_, v) => setTabValue(v)}
				sx={{ minHeight: 36, px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
				<Tab
					label="Documents"
					value="documents"
					sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, py: 0.5 }}
				/>
				<Tab
					label="Users"
					value="users"
					sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, py: 0.5 }}
				/>
			</Tabs>

			{tabValue === 'documents' &&
				(isLoadingDocs ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress size={24} />
					</Box>
				) : documents.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 2 }}>
						No documents found
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={headSx}>Name</TableCell>
								<TableCell sx={headSx}>Type</TableCell>
								<TableCell sx={headSx}>Uploaded By</TableCell>
								<TableCell sx={headSx}>Created</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{documents.map((doc) => (
								<TableRow key={doc.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
									<TableCell sx={cellSx}>
										<Typography variant="body2" fontWeight={600}>
											{doc.name}
										</Typography>
									</TableCell>
									<TableCell sx={cellSx}>
										<Typography variant="body2" color="text.secondary">
											{doc.type && typeof doc.type !== 'number' ? doc.type.name : '—'}
										</Typography>
									</TableCell>
									<TableCell sx={cellSx}>
										<Typography variant="body2" color="text.secondary">
											{doc.user && typeof doc.user !== 'number' ? doc.user.fullName : '—'}
										</Typography>
									</TableCell>
									<TableCell sx={cellSx}>
										<Typography variant="body2" color="text.secondary">
											{formatDate({ date: doc.created })}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				))}

			{tabValue === 'users' &&
				(isLoadingUsers ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress size={24} />
					</Box>
				) : users.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 2 }}>
						No users found
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={headSx}>Name</TableCell>
								<TableCell sx={headSx}>Email</TableCell>
								<TableCell sx={headSx}>Role</TableCell>
								<TableCell sx={headSx}>Country</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{users.map((user) => (
								<TableRow key={user.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
									<TableCell sx={cellSx}>
										<Typography variant="body2" fontWeight={600}>
											{user.fullName || `${user.firstName} ${user.lastName}`}
										</Typography>
									</TableCell>
									<TableCell sx={cellSx}>
										<Typography variant="body2" color="text.secondary">
											{user.email || '—'}
										</Typography>
									</TableCell>
									<TableCell sx={cellSx}>
										<Typography variant="body2" color="text.secondary">
											{user.role || '—'}
										</Typography>
									</TableCell>
									<TableCell sx={cellSx}>
										<Typography variant="body2" color="text.secondary">
											{user.country && typeof user.country !== 'number' ? user.country.name : '—'}
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				))}
		</Box>
	);
};

// Component

export const BeneficiariesDataTable = ({
	beneficiaries,
	isLoading = false,
	searchValue = '',
	onSearchChange,
	orderBy = 'created',
	order = 'DESC',
	onSortChange,
	page = 1,
	rowsPerPage = 10,
	totalCount = 0,
	onPageChange,
	onRowsPerPageChange,
	filterValues = {},
	onFilterChange,
	onEditBeneficiary,
	onDeactivateBeneficiary,
	onUploadDocuments,
	customFieldDefinitions = [],
}: BeneficiariesDataTableProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { project } = useContext(ContextProject);

	const handleRowClick = useCallback(
		(entity: Entity) => {
			navigate(`/project/${project?.idWbg}/spend-plans/beneficiary/${entity.id}`);
		},
		[navigate, project]
	);

	// Column Definitions

	const columns: ColumnDef<Entity>[] = useMemo(() => {
		const baseCols: ColumnDef<Entity>[] = [
			{
				id: 'name',
				label: t('name'),
				sortKey: 'name',
				minWidth: 220,
				sortable: true,
				render: (entity) => (
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<Avatar
							sx={{
								bgcolor: alpha(theme.palette.primary.main, 0.12),
								color: theme.palette.primary.main,
								height: 28,
								width: 28,
							}}>
							<IconBeneficiary sx={{ fontSize: 16 }} />
						</Avatar>
						<Box sx={{ minWidth: 0, maxWidth: 180 }}>
							<TruncatedText
								text={entity.name}
								variant="body2"
								sx={{ fontWeight: 700, lineHeight: 1.3 }}
							/>
							{entity.idExternal && (
								<TruncatedText variant="caption" color="text.secondary" text={entity.idExternal} />
							)}
						</Box>
					</Stack>
				),
			},
			{
				id: 'subtype',
				label: t('type'),
				minWidth: 100,
				sortable: false,
				render: (entity) => (
					<Typography variant="body2" color="text.secondary">
						{entity.subtype || '—'}
					</Typography>
				),
			},
			{
				id: 'parent',
				label: t('entity'),
				minWidth: 140,
				sortable: false,
				render: (entity) => {
					const parentEntity = entity.parent;
					if (!parentEntity || typeof parentEntity === 'number')
						return (
							<Typography variant="body2" color="text.secondary">
								—
							</Typography>
						);
					return (
						<Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, maxWidth: 130 }}>
							<IconBusiness sx={{ color: 'text.secondary', fontSize: 14, flexShrink: 0 }} />
							<TruncatedText variant="body2" color="text.secondary" text={parentEntity.name} />
						</Stack>
					);
				},
			},
			{
				id: 'status',
				label: t('status'),
				minWidth: 100,
				align: 'center',
				sortable: false,
				render: (entity) => {
					const walletStatus = entity.wallet?.status || 'ACTIVE';
					const statusConfig = getStatusConfig(walletStatus);
					return (
						<Chip
							icon={<statusConfig.icon sx={{ fontSize: '14px !important', color: 'inherit' }} />}
							label={statusConfig.label}
							size="small"
							sx={{
								bgcolor: alpha(statusConfig.color, 0.1),
								color: statusConfig.color,
								fontWeight: 600,
								'& .MuiChip-icon': { color: 'inherit' },
							}}
						/>
					);
				},
			},
		];

		// Custom field columns
		const activeCustomFields = customFieldDefinitions.filter((c) => c.isActive);
		const customCols: ColumnDef<Entity>[] = activeCustomFields.map((cf) => ({
			id: `cf_${cf.id}`,
			label: cf.name,
			minWidth: 120,
			sortable: false,
			render: (entity: Entity) => {
				const value = entity.customData?.[cf.id];
				return (
					<Typography variant="body2" color="text.secondary">
						{value != null ? String(value) : '—'}
					</Typography>
				);
			},
		}));

		const trailingCols: ColumnDef<Entity>[] = [
			{
				id: 'modified',
				label: t('updated'),
				sortKey: 'modified',
				minWidth: 110,
				sortable: true,
				render: (entity) => (
					<Typography variant="body2" color="text.secondary">
						{formatDate({ date: entity.modified })}
					</Typography>
				),
			},
		];

		return [...baseCols, ...customCols, ...trailingCols];
	}, [t, theme, customFieldDefinitions]);

	// Filters

	const filters: FilterConfig[] = useMemo(
		() => [
			{
				key: 'subtype',
				label: t('type'),
				options: [
					{ label: 'All Types', value: 'ALL' },
					{ label: t('organization'), value: 'ORGANIZATION' },
					{ label: t('individual'), value: 'INDIVIDUAL' },
				],
			},
		],
		[t]
	);

	// Row Actions

	const rowActions: RowAction<Entity>[] = useMemo(
		() => [
			{
				label: 'Upload Documents',
				icon: <IconUpload sx={{ fontSize: 18 }} />,
				onClick: (entity) => onUploadDocuments?.(entity),
			},
			{
				label: 'See Details',
				icon: <IconView sx={{ fontSize: 18 }} />,
				onClick: (entity) => handleRowClick(entity),
			},
			{
				label: 'Edit Beneficiary',
				icon: <IconEdit sx={{ fontSize: 18 }} />,
				onClick: (entity) => onEditBeneficiary?.(entity),
				dividerBefore: true,
			},
			{
				label: 'Deactivate Beneficiary',
				icon: <IconDeactivate sx={{ fontSize: 18 }} />,
				onClick: (entity) => onDeactivateBeneficiary?.(entity),
				show: (entity) => entity.wallet?.status !== 'INACTIVE',
				destructive: true,
				dividerBefore: true,
			},
			{
				label: 'Activate Beneficiary',
				icon: <IconActivate sx={{ fontSize: 18 }} />,
				onClick: (entity) => onDeactivateBeneficiary?.(entity),
				show: (entity) => entity.wallet?.status === 'INACTIVE',
				dividerBefore: true,
			},
		],
		[onUploadDocuments, onEditBeneficiary, onDeactivateBeneficiary, handleRowClick]
	);

	return (
		<DataTable<Entity>
			data={beneficiaries}
			columns={columns}
			getRowKey={(entity) => entity.id}
			loading={isLoading}
			emptyMessage={t('empty.beneficiary.title')}
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			searchPlaceholder={t('search')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			showFilters
			filters={filters}
			filterValues={filterValues}
			onFilterChange={onFilterChange}
			rowActions={rowActions}
			onRowClick={handleRowClick}
			expandable
			renderExpanded={(entity) => <ExpandedBeneficiaryRow entity={entity} />}
		/>
	);
};
